import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import { message } from "antd";
import { User } from "./useUsers";

// Types for roles and permissions
export interface UserRole {
  id: number;
  user_id: number;
  role_id: number;
  created_at: string;
  updated_at: string;
  deleted_at: null | string;
}

export interface RolePermission {
  id: number;
  role_id: number;
  permission_id: number;
  status: number;
  created_at: string;
  updated_at: string;
  deleted_at: null | string;
}

export interface Role {
  id: number;
  name: string;
  description: string;
  total_users?: number;
  permissions: RolePermission[];
}

// Enhanced user type maintained for backward compatibility
export interface EnhancedUser extends User {
  // We maintain the roleInfo property for backward compatibility
  roleInfo?: {
    userRole: UserRole;
    role: Role;
    permissions: Record<number, boolean>; // permission_id -> enabled
  };
  // Store the role ID for quick access
  roleId?: number;
  // Include roles from the direct API response
  roles?: {
    id: number;
    name: string;
    description: string;
    total_users?: number;
    permissions: RolePermission[];
  };
}

// Fetch Current User from LocalStorage
const fetchCurrentUser = async (): Promise<EnhancedUser | null> => {
  const userData = localStorage.getItem("user");
  return userData ? JSON.parse(userData) : null;
};

// React Query Hook for Authentication
export const useAuth = () => {
  return useQuery({
    queryKey: ["currentUser"],
    queryFn: fetchCurrentUser,
    staleTime: 1000 * 60 * 10,
    retry: false,
  });
};

// Process permissions from the login response to get a permission map
const processPermissions = (role: Role): Record<number, boolean> => {
  const permissionsMap: Record<number, { status: boolean; updatedAt: string }> =
    {};

  // Process permissions to get the latest status for each
  role.permissions.forEach((permission) => {
    const permId = permission.permission_id;

    // If we haven't seen this permission yet, or this one is newer, update the map
    if (
      !permissionsMap[permId] ||
      new Date(permission.updated_at) >
        new Date(permissionsMap[permId].updatedAt)
    ) {
      permissionsMap[permId] = {
        status: permission.status === 1,
        updatedAt: permission.updated_at,
      };
    }
  });

  // Convert to simple permission_id -> boolean map
  const result: Record<number, boolean> = {};
  Object.entries(permissionsMap).forEach(([permId, data]) => {
    result[Number(permId)] = data.status;
  });

  return result;
};

// React Query Hook for Login
export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { user: EnhancedUser; access_token: string; message: string },
    Error,
    { email: string; password: string }
  >({
    mutationFn: async ({ email, password }) => {
      try {
        // Perform login
        const loginResponse = await apiClient.post("/auth/login", {
          email,
          password,
        });

        const { user, access_token } = loginResponse.data;

        // Set token immediately for subsequent requests
        localStorage.setItem("token", access_token);
        apiClient.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${access_token}`;

        // For backward compatibility, we maintain the roleInfo structure
        // while also keeping the original data from the API response
        const enhancedUser: EnhancedUser = {
          ...user,
        };

        // If roles data exists in the response, use it to create roleInfo
        if (user.roles) {
          // Process permissions to a map for faster access
          const permissions = processPermissions(user.roles);

          // Create roleInfo for backward compatibility
          enhancedUser.roleInfo = {
            userRole: {
              id: 0, // This will be replaced with actual data later
              user_id: user.id,
              role_id: user.roles.id,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              deleted_at: null,
            },
            role: user.roles,
            permissions,
          };

          // Store role ID for quick access
          enhancedUser.roleId = user.roles.id;
        }

        // Save user to localStorage
        localStorage.setItem("user", JSON.stringify(enhancedUser));

        return {
          user: enhancedUser,
          access_token,
          message: loginResponse.data.message || "Login successful",
        };
      } catch (error: any) {
        console.error("Login error:", error);

        // Extract the error message from the response if available
        const errorMessage =
          error.response?.data?.message ||
          "Login failed. Please check your credentials and try again.";

        throw new Error(errorMessage);
      }
    },
    onSuccess: (data) => {
      // Set the current user data in React Query cache
      queryClient.setQueryData(["currentUser"], data.user);

      // Invalidate the userPermissions query to trigger a fetch
      queryClient.invalidateQueries({ queryKey: ["userPermissions"] });

      // Show success message
      message.success(data?.message || "Login successful");

      // Redirect to dashboard
      window.location.href = "/dashboard";
    },
    onError: (error: any) => {
      console.error("Login error in mutation:", error);
    },
  });
};

// React Query Hook for Logout
export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error>({
    mutationFn: async () => {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      queryClient.invalidateQueries({ queryKey: ["userPermissions"] });
      window.location.href = "/login";
    },
  });
};
